/**
 * PATH: apps/hr/src/App.tsx
 * PURPOSE: Main application component for HR Hub
 * 
 * Uses @quotech/ui DashboardLayout for unified navigation
 * matching the Jobs Hub and Workforce Hub patterns.
 */

import { useMemo, useState, useCallback } from 'react'
import { Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom'
import { DashboardLayout, TabBar, type User, getHubBreadcrumbs, buildSettingsTabs, useGlobalSearchBridge, useTabNavigation, useTabKeyboardShortcuts, ErrorBoundary, VersionWatcher } from '@quotech/ui'
import { useApiClient } from '@quotech/api'
import { ProtectedRoute, HubGuard, UnifiedSSOPage, useAuth, useAuthStore, useComputedIsAdmin, useRBAC, useHrFeatures } from '@quotech/auth'
import { FileText, Tag, Bell } from 'lucide-react'
import {
  MODULE_CONFIG,
  NAV_ITEMS,
  SECONDARY_NAV_ITEMS,
  EXTERNAL_LINKS,
  COMMANDS,
  HUB_LINKS,
  filterNavItemsByHrFeatures,
} from './config/navigation'
import { HR_TAB_CONFIG, useHrTabStore } from './config/tabs'

// Pages
import EmployeesPage from './pages/employees/EmployeesPage'
import EmployeeDetailPage from './pages/employees/EmployeeDetailPage'
import LeavePage from './pages/leave/LeavePage'
import PayrollPage from './pages/payroll/PayrollPage'
import PayRunDetailPage from './pages/payroll/PayRunDetailPage'
import SupportPage from './pages/support/SupportPage'
import TicketDetailPage from './pages/support/TicketDetailPage'
import DocumentsPage from './pages/documents/DocumentsPage'
import DocumentDetailPage from './pages/documents/DocumentDetailPage'
import ClockPage from './pages/clock/ClockPage'
import InboxPage from './pages/inbox/InboxPage'
import {
  SettingsPage,
  TagManagementPage,
  DocumentTypeManagementPage,
  DocumentAlertPoliciesPage,
} from './pages/settings'

// Settings Tab Content Components (embedded versions for the modal)
import { DocumentTypeSettingsTab } from './components/settings/DocumentTypeSettingsTab'
import { TagSettingsTab } from './components/settings/TagSettingsTab'
import { AlertPoliciesSettingsTab } from './components/settings/AlertPoliciesSettingsTab'

/**
 * Main layout wrapper using @quotech/ui
 */
function HRLayout() {
  const location = useLocation()
  const [settingsOpen, setSettingsOpen] = useState(false)
  const apiClient = useApiClient()

  // Auth user (required for real org context + permissions)
  const { user, logout, organisationId, organisationName } = useAuth()

  const computedIsAdmin = useComputedIsAdmin(user as Record<string, any> | null)
  const { accessibleModules, permissions: rbacPermissions, hierarchyLevel } = useRBAC()

  // HR feature flags drive which nav items render. Phase 2 of
  // HR_UNIFICATION_PLAN.md — single source of truth for what HR
  // surfaces a tenant sees.
  const { features: hrFeatures } = useHrFeatures()
  const filteredNavItems = useMemo(
    () => filterNavItemsByHrFeatures(NAV_ITEMS, hrFeatures),
    [hrFeatures],
  )

  // Map auth user to UI user format - includes ALL admin-related fields
  const uiUser: User & { displayEmail?: string } | null = useMemo(() => {
    if (!user) return null
    return {
      id: user.id,
      name: user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
      email: user.email,
      // Header-only authority display; login identity stays on `email`.
      displayEmail: user.display_email || user.authority_email || user.email,
      organisation: organisationName || user.organisation_name,
      organisationId: organisationId || user.organisation_id,
      role: user.role,
      isAdmin: computedIsAdmin,
      admin: (user as Record<string, unknown>).admin as boolean | undefined,
      is_admin: (user as Record<string, unknown>).is_admin as boolean | undefined,
      is_staff: (user as Record<string, unknown>).is_staff as boolean | undefined,
      is_superuser: (user as Record<string, unknown>).is_superuser as boolean | undefined,
    }
  }, [organisationId, organisationName, user, computedIsAdmin])

  // Build breadcrumbs using shared utility
  const breadcrumbs = getHubBreadcrumbs('hr', location.pathname)
  
  // Global Search Bridge - provides API client with auth for search to work
  const tokenProvider = useMemo(() => ({
    getAccessToken: () => useAuthStore.getState().accessToken,
    getOrgId: () => useAuthStore.getState().selectedOrg?.id || organisationId,
  }), [organisationId])
  
  const { apiClient: searchApiClient } = useGlobalSearchBridge({
    tokenProvider,
    onSettingsOpen: useCallback(() => setSettingsOpen(true), []),
  })

  // Tab navigation (browser-style multi-tab)
  const tabNav = useTabNavigation(HR_TAB_CONFIG)
  useTabKeyboardShortcuts({ useStore: useHrTabStore, ...tabNav })

  // Update settings nav item to open modal
  const secondaryNavItems = useMemo(() => 
    SECONDARY_NAV_ITEMS.map(item => 
      item.id === 'settings' 
        ? { ...item, action: () => setSettingsOpen(true) }
        : item
    ),
    []
  )

  // Build settings tabs with HR-specific document settings
  const settingsTabs = useMemo(() => buildSettingsTabs({
    user: user ? {
      id: user.id,
      first_name: user.first_name || user.name?.split(' ')[0],
      last_name: user.last_name || user.name?.split(' ').slice(1).join(' '),
      email: user.email,
      organisation_name: user.organisation_name || organisationName,
      role: user.role,
      isAdmin: computedIsAdmin,
      permissions: rbacPermissions, hierarchy_level: hierarchyLevel,
    } : null,
    hubId: 'hr',
    hubName: 'HR Hub',
    apiClient,
    organisationId: organisationId || user?.organisation_id,
    profileData: {
      first_name: user?.first_name || user?.name?.split(' ')[0],
      last_name: user?.last_name || user?.name?.split(' ').slice(1).join(' '),
      email: user?.email,
      organisation_name: user?.organisation_name || organisationName,
    },
    organisationData: {
      id: organisationId || user?.organisation_id || '',
      name: organisationName || user?.organisation_name || 'Organisation',
    },
    integrations: [],
    // HR-specific settings tabs for Documents & OCR
    extraTabs: [
      {
        id: 'document-types',
        label: 'Document Types',
        icon: FileText,
        category: 'Documents & OCR',
        description: 'Manage document types and OCR settings',
        adminOnly: true,
        content: <DocumentTypeSettingsTab />,
      },
      {
        id: 'tags',
        label: 'Tag Management',
        icon: Tag,
        category: 'Documents & OCR',
        description: 'Create and manage document tags',
        adminOnly: true,
        content: <TagSettingsTab />,
      },
      {
        id: 'alert-policies',
        label: 'Alert Policies',
        icon: Bell,
        category: 'Documents & OCR',
        description: 'Configure expiry alerts and notifications',
        adminOnly: true,
        content: <AlertPoliciesSettingsTab />,
      },
    ],
  }), [apiClient, user, organisationId, organisationName, computedIsAdmin, rbacPermissions, hierarchyLevel])

  return (
    <DashboardLayout
      navItems={filteredNavItems}
      secondaryNavItems={secondaryNavItems}
      externalLinks={EXTERNAL_LINKS}
      module={MODULE_CONFIG}
      accessibleModules={accessibleModules}
      user={uiUser}
      onLogout={logout}
      breadcrumbs={breadcrumbs}
      showGlobalSearch={true}
      globalSearchApiClient={searchApiClient}
      showCommandPalette
      commands={COMMANDS}
      hubLinks={HUB_LINKS}
      showOrgSwitcher
      showHubSwitcher
      headerTabsSlot={
        <TabBar
          variant="inline"
          tabs={tabNav.tabItems}
          activeTabId={tabNav.activeTabId}
          onTabClick={tabNav.handleTabClick}
          onTabClose={tabNav.handleTabClose}
          onNewTab={tabNav.handleNewTab}
          onTabPin={tabNav.handleTabPin}
          onCloseOthers={tabNav.handleCloseOthers}
          onDuplicateTab={tabNav.handleDuplicateTab}
          accentColor="bg-purple-500"
        />
      }
      sidebarSectionLabels={{
        main: 'HR',
        secondary: 'Tools',
        hubs: 'Other Hubs',
      }}
      contentClassName="px-6 py-4"
      showSettingsModal
      settingsTabs={settingsTabs}
      settingsModalOpen={settingsOpen}
      onCloseSettings={() => setSettingsOpen(false)}
      onOpenSettings={() => setSettingsOpen(true)}
    >
      <Outlet />
    </DashboardLayout>
  )
}

/**
 * Main App with routing
 */
export default function App() {
  return (
    <ErrorBoundary>
    {/* Auto-update notifier — see qf!2578 for context (30s poll, 10-min idle auto-reload). */}
    <VersionWatcher pollIntervalMs={30_000} autoReloadAfterMs={null} />
    <Routes>
      {/* Unified SSO Route - standardized across all hubs */}
      <Route
        path="/sso"
        element={
          <UnifiedSSOPage
            defaultRedirectPath="/"
            loadingComponent={
              <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
                <div className="text-center">
                  <div className="w-10 h-10 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto" />
                  <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">Signing you in to HR...</p>
                </div>
              </div>
            }
          />
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute useMasterLogin>
            <HubGuard hub="hr"><HRLayout /></HubGuard>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="employees" replace />} />
        <Route path="employees" element={<EmployeesPage />} />
        <Route path="employees/:id" element={<EmployeeDetailPage />} />
        {/* Time clock — gated on useHrFeatures().timeclock inside the page. */}
        <Route path="clock" element={<ClockPage />} />
        {/* Supervisor approval queue — gated on useHrFeatures().approvals
            inside the page (Phase 3 of HR_UNIFICATION_PLAN.md). */}
        <Route path="inbox" element={<InboxPage />} />
        <Route path="leave" element={<LeavePage />} />
        <Route path="payroll" element={<PayrollPage />} />
        <Route path="payroll/:id" element={<PayRunDetailPage />} />
        <Route path="support" element={<SupportPage />} />
        <Route path="support/:id" element={<TicketDetailPage />} />
        <Route path="documents" element={<DocumentsPage />} />
        <Route path="documents/:id" element={<DocumentDetailPage />} />
        <Route path="compliance" element={<Navigate to="/employees" replace />} />
        {/* Settings Routes */}
        <Route path="settings" element={<SettingsPage />} />
        <Route path="settings/tags" element={<TagManagementPage />} />
        <Route path="settings/document-types" element={<DocumentTypeManagementPage />} />
        <Route path="settings/alert-policies" element={<DocumentAlertPoliciesPage />} />
        <Route path="*" element={<Navigate to="/employees" replace />} />
      </Route>
    </Routes>
    </ErrorBoundary>
  )
}
